import { useEffect, useState } from "react";

import MOBILE, { BREAKPOINT_PX, ROOT_CLASS, IOS_CLASS } from "./mobileConfig";
import { startDateLayer, stopDateLayer } from "./dateLayer";

/**
 * Is this a mobile viewport, and is the mobile layer switched on?
 *
 * Returns false whenever MOBILE.ENABLED is false, no matter how narrow the
 * window is — so the master flag really is master, and a desktop-only debugging
 * session cannot be confused by a half-applied layout.
 *
 * SIDE EFFECT, and it is the point: while any mounted component holds a true
 * result, `zy-mobile` sits on <html>. Every rule in styles/mobile.scss is
 * written under that class AND inside a max-width query, so the stylesheet is
 * inert in two independent ways when the flag is off. Belt and braces, because
 * the one thing that must never happen here is a mobile rule reaching desktop.
 *
 * The class is reference-counted rather than set-and-cleared: several mobile
 * components call this hook, and the first one to unmount must not strip the
 * class out from under the others.
 */

/** How many live subscribers currently want the root class. */
let holders = 0;

/** Told whenever the root class goes on or comes off — see useMobileLayer. */
const layerListeners = new Set();
const notifyLayer = () => layerListeners.forEach((fn) => fn(holders > 0));

const query = () => `(max-width: ${BREAKPOINT_PX}px)`;

/** SSR-safe, and safe in the jsdom used by react-scripts test. */
const matches = () => {
  if (!MOBILE.ENABLED) return false;
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia(query()).matches;
};

/**
 * WebKit on iOS — every iPhone browser, since all of them are WebKit — and
 * nothing else: `-webkit-touch-callout` exists there alone, not on Android,
 * not in desktop Safari. Read when the class is stamped, not at module load.
 * Also read by iosInputZoom.js, so iOS is detected in one place.
 */
export const isIOSWebKit = () =>
  typeof window !== "undefined" &&
  !!window.CSS &&
  typeof window.CSS.supports === "function" &&
  window.CSS.supports("-webkit-touch-callout", "none");

/**
 * The same answer as useIsMobile, WITHOUT its side effect: no root class, no
 * date layer. For a screen that wants one mobile-only attribute but has no
 * mobile layout — the login page, where putting `zy-mobile` on <html> would
 * start applying mobile.scss to a screen that was never designed for it.
 */
export const useMobileQuery = () => {
  const [isMobile, setIsMobile] = useState(matches);

  useEffect(() => {
    if (!MOBILE.ENABLED) return undefined;
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mql = window.matchMedia(query());
    const onChange = () => setIsMobile(mql.matches);
    onChange();

    /* addListener is deprecated but it is what Safari < 14 has, and this app
       still supports react-scripts 3 era browsers. Prefer the modern one and
       fall back rather than picking either outright. */
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
    } else {
      mql.addListener(onChange);
    }

    return () => {
      if (typeof mql.removeEventListener === "function") {
        mql.removeEventListener("change", onChange);
      } else {
        mql.removeListener(onChange);
      }
    };
  }, []);

  return isMobile;
};

const useIsMobile = () => {
  const isMobile = useMobileQuery();

  useEffect(() => {
    if (!isMobile || typeof document === "undefined") return undefined;
    const root = document.documentElement;
    holders += 1;
    root.classList.add(ROOT_CLASS);
    /* iOS alone, for the rules that must reach an iPhone and nothing else —
       today, date fields sized by the form rather than by WebKit (mobile.scss). */
    if (MOBILE.iosDateFields && isIOSWebKit()) root.classList.add(IOS_CLASS);
    /* The native date fields' mobile face runs exactly as long as the mobile
       view does — started with the root class, stopped with it. Both calls are
       idempotent, so a screen swap (one holder leaving as another arrives)
       costs a rescan at most. See dateLayer.js. */
    startDateLayer();
    notifyLayer();
    return () => {
      holders -= 1;
      if (holders <= 0) {
        holders = 0;
        root.classList.remove(ROOT_CLASS);
        root.classList.remove(IOS_CLASS);
        stopDateLayer();
        notifyLayer();
      }
    };
  }, [isMobile]);

  return isMobile;
};

/**
 * Is the mobile layer ON right now — is some screen holding the root class?
 * Reads, never stamps.
 *
 * For a component that sits OUTSIDE every screen and must follow the layer
 * rather than the width. Today that is SparkTGCallFloater, mounted once at the
 * App root: useIsMobile there would stamp `zy-mobile` on every route, Health
 * plan and Book appointment included, which render their desktop tree at phone
 * width and were never designed for mobile.scss. useMobileQuery would say
 * "phone" on those routes too, where the mobile row has no styles to draw with.
 *
 * Re-read once on subscribe, because a screen's effect can run before this one.
 * Never true with ENABLED off — nothing becomes a holder.
 */
export const useMobileLayer = () => {
  const [on, setOn] = useState(() => holders > 0);

  useEffect(() => {
    layerListeners.add(setOn);
    setOn(holders > 0);
    return () => {
      layerListeners.delete(setOn);
    };
  }, []);

  return on;
};

export default useIsMobile;
